import React, { useMemo } from 'react';

import { useGetStreams, useGetLists } from '@vlabs/api/artemy/client';
import type { EventStatFilterValue } from '@vlabs/api/artemy/model';
import {
  GENDER_OPTIONS, MASK_OPTIONS, DEEPFAKE_STATE_OPTIONS, EMOTION_OPTIONS,
} from '@vlabs/api-bindings/src/constants';
import { LIVENESS_OPTIONS, EVENT_ENUMS } from '@vlabs/api-bindings/src/luna-client/constants';
import { useChartFormTranslation } from '@vlabs/monokit/entities/charts/chart-form/i18n';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';
import validate from '@vlabs/shared/validators';
import { Control, SettingsItemWrapper } from '@vlabs/uikit';
import { useFormContext, Controller } from 'react-hook-form';

import st from './FiltersForm.module.sass';
import { GeopositionFilterSubForm } from './GeopositionFilterSubForm';
import type { GeoPositionBbox } from './GeopositionFilterSubForm';

interface FilterValueControlProps {
  disabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldConfig: Record<string, any>;
  filterIndex: number;
  value: EventStatFilterValue;
}

export function FilterValueControl({
  disabled,
  fieldConfig,
  filterIndex,
  value,
}: FilterValueControlProps): React.JSX.Element {
  const { t } = useChartFormTranslation();
  const {
    control,
    clearErrors,
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { data: streamsData } = useGetStreams();
  const camOptions = useMemo(() => {
    const streams = streamsData?.streams ?? [];
    // ponytail: empty group when no streams, backend handles permissions
    return [{
      label: 'Luna Streams',
      options: streams.map((s) => ({
        streamId: s.stream_id,
        value: s.name,
        label: s.name,
      })),
    }];
  }, [streamsData]);

  const { data: listsData } = useGetLists({ page_size: 1000 });
  const listOptions = listsData?.lists?.map((list) => ({
    label: list.user_data,
    value: list.list_id,
  })) ?? [];

  const optionsForSelect = {
    lists: listOptions,
    source: camOptions,
    gender: GENDER_OPTIONS.raw,
    emotion: EMOTION_OPTIONS.raw,
    mask: MASK_OPTIONS.raw,
    liveness: LIVENESS_OPTIONS.raw,
    deepfake: DEEPFAKE_STATE_OPTIONS.raw,
    apparent_gender: EVENT_ENUMS.BODY_BASIC_ATTRIBUTES.APPARENT_GENDER.raw,
    sleeve_length: EVENT_ENUMS.UPPER_BODY.SLEEVE.raw,
    headwear_state: EVENT_ENUMS.UPPER_BODY.HEADWEAR.raw,
    upper_clothing_colors: EVENT_ENUMS.UPPER_BODY.UPPER_CLOSING_COLOR.raw,
    backpack_state: EVENT_ENUMS.ACCESSORIES.BACKPACK.raw,
    lower_garment_colors: EVENT_ENUMS.LOWER_BODY.LOWER_GARMENT.COLORS.raw,
    headwear_apparent_color: EVENT_ENUMS.UPPER_BODY.HEADWEAR_COLORS.raw,
    lower_garment_type: EVENT_ENUMS.LOWER_BODY.LOWER_GARMENT.TYPE.raw,
    shoes_apparent_color: EVENT_ENUMS.LOWER_BODY.SHOES.APPARENT_COLOR.raw,
  };

  const name = `getEventStats.filters.${filterIndex}.value`;
  const handleGeolocationChange = (newValue: GeoPositionBbox) => {
    setValue(name, newValue);
    clearErrors(name);
  };

  const renderValueControl = () => {
    if (fieldConfig?.format === 'select') {
      const { optionsKey } = fieldConfig;

      return (
        <ControllableValueSelect
          control={control}
          disabled={disabled}
          errors={errors}
          isMulti={fieldConfig.isMulti}
          name={name}
          options={optionsForSelect?.[optionsKey as keyof typeof optionsForSelect]}
          rules={{
            required: validate.required(),
            validate: null,
          }}
        />
      );
    }

    if (fieldConfig?.format === 'geolocation') {
      return (
        <React.Fragment>
          <GeopositionFilterSubForm
            disabled={disabled}
            initialValues={value as GeoPositionBbox}
            onSubmit={handleGeolocationChange}
          />
          <Control.Checkbox
            className={st.Hidden}
            errors={errors}
            id={name}
            {...register(name, {
              required: validate.required(),
              validate: null,
            })}
          />
        </React.Fragment>
      );
    }

    if (fieldConfig?.format === 'input') {
      return (
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState: { error } }) => (
            <Control.Input
              {...field}
              disabled={disabled}
              errors={{ [name]: error }}
              id={name}
            />
          )}
          rules={{
            required: validate.required(),
            validate: fieldConfig?.validateRule?.(),
          }}
        />
      );
    }

    return (
      <Control.Input
        disabled
      />
    );
  };

  return (
    <SettingsItemWrapper
      contentClassName={st.SettingsItem__Content}
      data-tooltip-content={(disabled && t('form.filters.value.tooltip')) as unknown as string}
      disabled={disabled}
      title={t('form.filters.value.label')}
      titleClassName={st.SettingsItem__Title}
    >
      {renderValueControl()}
    </SettingsItemWrapper>
  );
}
